const API_BASE = String(
  import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.MODE === "development" ? "http://localhost:8787" : ""),
).replace(/\/+$/, "");

export function backendConfigured() {
  return Boolean(API_BASE);
}

export function backendBaseUrl() {
  return API_BASE;
}

export async function checkBackend(signal) {
  if (!API_BASE) {
    return {
      status: "not-configured",
      features: { aiProxy: false, auditHistory: false, rateLimiting: false },
    };
  }
  return request("/api/health", { signal, timeout: 7000 });
}

export async function fetchProviderCatalog(signal) {
  const payload = await request("/api/providers", { signal, timeout: 10000 });
  return payload.data || [];
}

export async function validateProviderWithWorker(provider, apiKey, signal) {
  return request(`/api/ai/providers/${encodeURIComponent(provider)}/validate`, {
    method: "POST",
    providerKey: apiKey,
    signal,
    body: {},
  });
}

export async function fetchWorkerModels(provider, apiKey, signal) {
  const payload = await request(`/api/ai/providers/${encodeURIComponent(provider)}/models`, {
    method: "POST",
    providerKey: apiKey,
    signal,
    timeout: 30000,
    body: {},
  });
  return payload.data || [];
}

export async function runWorkerAudit({ provider, apiKey, model, auditPayload, signal }) {
  return request("/api/ai/review", {
    method: "POST",
    providerKey: apiKey,
    signal,
    timeout: 90000,
    body: { provider, model, auditPayload },
  });
}

export async function runWorkerFollowUp({
  provider,
  apiKey,
  model,
  auditPayload,
  priorReview,
  conversation,
  question,
  signal,
}) {
  return request("/api/ai/follow-up", {
    method: "POST",
    providerKey: apiKey,
    signal,
    timeout: 90000,
    body: { provider, model, auditPayload, priorReview, conversation, question },
  });
}

export async function listAuditHistory(workspaceToken, signal) {
  const payload = await request("/api/audits?limit=30", {
    workspaceToken,
    signal,
  });
  return payload.data || [];
}

export async function saveAuditHistory(snapshot, workspaceToken, signal) {
  return request("/api/audits", {
    method: "POST",
    workspaceToken,
    signal,
    body: snapshot,
  });
}

export async function deleteAuditHistory(id, workspaceToken, signal) {
  return request(`/api/audits/${encodeURIComponent(id)}`, {
    method: "DELETE",
    workspaceToken,
    signal,
  });
}

async function request(
  path,
  {
    method = "GET",
    body,
    providerKey,
    workspaceToken,
    signal,
    timeout = 30000,
  } = {},
) {
  if (!API_BASE) throw new Error("Cloudflare Worker URL is not configured.");
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeout);
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        Accept: "application/json",
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(providerKey ? { "X-AI-Provider-Key": providerKey.trim() } : {}),
        ...(workspaceToken ? { "X-Workspace-Token": workspaceToken } : {}),
      },
      credentials: "omit",
      signal: controller.signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload = await readJson(response);
    if (!response.ok) {
      const error = new Error(
        payload?.error?.message || `Backend request failed with HTTP ${response.status}.`,
      );
      error.code = payload?.error?.code;
      error.requestId = payload?.error?.requestId;
      throw error;
    }
    return payload;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

async function readJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("The backend returned an unreadable response.");
  }
}
